"use client";

import React, { useState } from "react";

/**
 * Normalize a single manufacturer string for display:
 *   - trim
 *   - strip trailing commas (e.g. "Dr.Reddy's Laboratories Inc.,")
 *   - ensure spacing after commas inside the name (e.g. "Pharmaceuticals,Inc." -> "Pharmaceuticals, Inc.")
 *   - collapse repeated whitespace
 */
const cleanName = (raw) =>
  (raw || "")
    .toString()
    .trim()
    .replace(/,+$/g, "")
    .replace(/,(\S)/g, ", $1")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Renders a list of manufacturer names. Each name is separated by a
 * middle-dot (·) so that internal commas inside legal names (e.g.
 * "Genentech, Inc.") aren't ambiguous with the list separator.
 *
 * Truncates to `previewCount` names on initial render; a "+N more" button
 * expands the rest inline. If `previewCount` covers everything, no button
 * is shown.
 */
const ManufacturerList = ({
  manufacturers = [],
  fallback = "",
  previewCount = 3,
  textClassName = "",
}) => {
  const [expanded, setExpanded] = useState(false);

  const cleaned = (Array.isArray(manufacturers) ? manufacturers : [])
    .map(cleanName)
    .filter(Boolean);

  // Nothing useful in the array? render fallback as-is.
  if (cleaned.length === 0) {
    return (
      <span className={textClassName}>{cleanName(fallback) || "Not Available"}</span>
    );
  }

  const visible = expanded ? cleaned : cleaned.slice(0, previewCount);
  const hiddenCount = cleaned.length - visible.length;

  return (
    <span className={textClassName}>
      {visible.join(", ")}
      {hiddenCount > 0 && !expanded && (
        <>
          {" "}
          <button
            type="button"
            onClick={(e) => {
              // The rep card and product tiles that host this list are
              // themselves clickable; expanding must not also open them.
              e.stopPropagation();
              e.preventDefault();
              setExpanded(true);
            }}
            className="ml-1 text-primary-primaryBlue hover:underline font-medium normal-case"
            aria-label={`Show ${hiddenCount} more manufacturers`}
          >
            +{hiddenCount} more
          </button>
        </>
      )}
      {expanded && cleaned.length > previewCount && (
        <>
          {" "}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setExpanded(false);
            }}
            className="ml-1 text-primary-primaryBlue hover:underline font-medium normal-case"
            aria-label="Show fewer manufacturers"
          >
            show less
          </button>
        </>
      )}
    </span>
  );
};

export default ManufacturerList;
